"use client";

/**
 * AlertLogsModal — the owner's "scrollback" of past dashboard runs.
 *
 * Opens from the Logs button in the owner AlertTopBar. Two-column layout:
 *   ┌──────────────┬──────────────────────────────────────┐
 *   │ run list     │ selected snapshot HTML rendered here │
 *   │ (newest      │ (sandboxed iframe so any inline       │
 *   │  first)      │  styles from the dashboard don't      │
 *   │              │  bleed into the rest of the app)      │
 *   └──────────────┴──────────────────────────────────────┘
 *
 * Loads the lightweight metadata list (no HTML) on open, then fetches
 * individual snapshot HTML one at a time when the user clicks a row.
 * Keeps the initial open snappy even if there are 30 × 100KB snapshots.
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  X,
  History,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  LayoutDashboard,
} from "lucide-react";
import {
  apiClient,
  type AlertSnapshot,
  type AlertSnapshotMeta,
} from "@/lib/api-client";
import { DashboardImage } from "@/components/notebook/DashboardImage";

interface AlertLogsModalProps {
  open: boolean;
  onClose: () => void;
  alertId: string;
  alertName: string;
  viewMode?: string;
}

export function AlertLogsModal({
  open,
  onClose,
  alertId,
  alertName,
  viewMode,
}: AlertLogsModalProps) {
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const [snapshots, setSnapshots] = useState<AlertSnapshotMeta[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [listLoading, setListLoading] = useState(false);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedSnap, setSelectedSnap] = useState<AlertSnapshot | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  // Fetch metadata list on open. We reset state on every open so a deleted
  // alert + reopened modal doesn't show stale snapshots.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setSnapshots(null);
    setSelectedId(null);
    setSelectedSnap(null);
    setListError(null);
    setListLoading(true);
    console.info("[AlertLogsModal] fetching list", { alertId });
    apiClient
      .getAlertSnapshotsList(alertId)
      .then((r) => {
        if (cancelled) return;
        setSnapshots(r.snapshots || []);
        // Auto-select the newest so the right panel isn't empty.
        if (r.snapshots && r.snapshots.length > 0) {
          setSelectedId(r.snapshots[0].id);
        }
      })
      .catch((err: Error) => {
        if (cancelled) return;
        console.error("[AlertLogsModal] list failed", { err });
        setListError(err.message || "Could not load past runs");
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, alertId]);

  // Fetch the selected snapshot's full HTML whenever `selectedId` changes.
  useEffect(() => {
    if (!selectedId || !open) return;
    let cancelled = false;
    setSelectedSnap(null);
    setDetailError(null);
    setDetailLoading(true);
    console.info("[AlertLogsModal] fetching snapshot", { alertId, selectedId });
    apiClient
      .getAlertSnapshot(alertId, selectedId)
      .then((r) => {
        if (cancelled) return;
        setSelectedSnap(r.snapshot);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        console.error("[AlertLogsModal] detail failed", { err });
        setDetailError(err.message || "Could not load this snapshot");
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedId, open, alertId]);

  const hasSnapshots = Boolean(snapshots && snapshots.length > 0);

  if (!mounted) return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-3"
          onClick={onClose}
        >
          <motion.div
            key="card"
            data-testid="alert-logs-modal"
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="relative w-full max-w-[980px] h-[min(84vh,720px)] rounded-2xl border border-slate-300/20 bg-slate-950 shadow-2xl overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader alertName={alertName} onClose={onClose} viewMode={viewMode} />

            <div className="flex-1 min-h-0 grid grid-cols-[260px_1fr]">
              <RunList
                snapshots={snapshots}
                listLoading={listLoading}
                listError={listError}
                selectedId={selectedId}
                onSelect={setSelectedId}
                viewMode={viewMode}
              />
              <DetailPane
                detailLoading={detailLoading}
                detailError={detailError}
                snapshot={selectedSnap}
                hasSnapshots={hasSnapshots}
                listLoading={listLoading}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponents
// ─────────────────────────────────────────────────────────────────────────────

function ModalHeader({
  alertName,
  onClose,
  viewMode,
}: {
  alertName: string;
  onClose: () => void;
  viewMode?: string;
}) {
  return (
    <div className="flex items-center justify-between px-5 h-14 border-b border-slate-800 bg-slate-950/90">
      <div className="flex items-center gap-2.5 min-w-0">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${viewMode === "signal" ? "bg-emerald-500/15 border border-emerald-400/30" : "bg-sky-500/15 border border-sky-400/30"}`}>
          <History className={`w-3.5 h-3.5 ${viewMode === "signal" ? "text-emerald-300" : "text-sky-300"}`} strokeWidth={2.2} />
        </div>
        <div className="flex flex-col min-w-0 leading-tight">
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-400">
            Past runs
          </span>
          <span className="text-sm font-semibold text-slate-100 truncate">
            {alertName}
          </span>
        </div>
      </div>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        data-testid="alert-logs-modal-close"
        className="text-slate-400 hover:text-slate-200 p-1.5 rounded-md hover:bg-slate-800 transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function RunList({
  snapshots,
  listLoading,
  listError,
  selectedId,
  onSelect,
  viewMode,
}: {
  snapshots: AlertSnapshotMeta[] | null;
  listLoading: boolean;
  listError: string | null;
  selectedId: string | null;
  onSelect: (id: string) => void;
  viewMode?: string;
}) {
  // `data-testid="alert-logs-modal-runs"` is the tour anchor for the
  // "scroll back through past runs" beat. Applied to whichever branch
  // ends up rendering so the selector is stable regardless of state
  // (loading / error / empty / populated). The user only sees this
  // sidebar AFTER opening the modal, so by the time the tour beat
  // fires, the populated branch is overwhelmingly the one in DOM.
  if (listLoading && !snapshots) {
    return (
      <div
        data-testid="alert-logs-modal-runs"
        className="border-r border-slate-800 flex items-center justify-center text-slate-400 text-[12px]"
      >
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
        Loading past runs…
      </div>
    );
  }

  if (listError) {
    return (
      <div
        data-testid="alert-logs-modal-runs"
        className="border-r border-slate-800 px-4 py-4 text-[12px] text-rose-300 flex items-start gap-2"
      >
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <span>{listError}</span>
      </div>
    );
  }

  if (!snapshots || snapshots.length === 0) {
    return (
      <div
        data-testid="alert-logs-modal-runs"
        className="border-r border-slate-800 px-4 py-6 text-center text-[12px] text-slate-400 flex flex-col items-center gap-2"
      >
        <Sparkles className="w-4 h-4 text-slate-500" />
        <span>No runs captured yet. Check back after the next run.</span>
      </div>
    );
  }

  return (
    <div
      data-testid="alert-logs-modal-runs"
      className="border-r border-slate-800 overflow-y-auto"
    >
      <ul className="divide-y divide-slate-800/70">
        {snapshots.map((s, index) => {
          const active = s.id === selectedId;
          return (
            <li key={s.id}>
              <button
                type="button"
                data-testid="alert-logs-modal-run-row"
                data-run-index={index}
                onClick={() => onSelect(s.id)}
                className={[
                  "w-full text-left px-3 py-2.5 transition-colors flex flex-col gap-0.5",
                  active
                    ? viewMode === "signal" ? "bg-emerald-500/10 border-l-2 border-emerald-400" : "bg-sky-500/10 border-l-2 border-sky-400"
                    : "border-l-2 border-transparent hover:bg-slate-800/50",
                ].join(" ")}
              >
                <div className="flex items-center gap-2 text-[12px] text-slate-100 font-medium tabular-nums">
                  <RunStatusDot status={s.run_status} />
                  <span>{formatRunAt(s.run_at)}</span>
                </div>
                <div className="flex items-center gap-2 pl-[20px]">
                  {s.is_first && (
                    <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-300/90 bg-amber-400/10 border border-amber-400/30 rounded px-1.5 py-0.5">
                      First
                    </span>
                  )}
                  <span className="text-[10.5px] text-slate-500 tabular-nums">
                    {formatBytes((s.executor_meta as any)?.html_size_bytes)}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DetailPane({
  detailLoading,
  detailError,
  snapshot,
  hasSnapshots,
  listLoading,
}: {
  detailLoading: boolean;
  detailError: string | null;
  snapshot: AlertSnapshot | null;
  hasSnapshots: boolean;
  listLoading: boolean;
}) {
  // `srcDoc` > `doc.write`: React's reconciler handles it declaratively,
  // the iframe is a FRESH document every time `snapshot.html` changes
  // (no stale DOM bleed-through), and we don't need refs or effects.
  // Previously we used doc.write inside a useEffect, but the iframe's
  // contentDocument was occasionally null at effect-time on rapid
  // row-clicks and the right pane came up empty. srcDoc never races.

  if (listLoading && !hasSnapshots) {
    return (
      <div className="flex items-center justify-center text-slate-400 text-[12px]">
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
        Loading…
      </div>
    );
  }

  if (!hasSnapshots) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-center px-6">
        <LayoutDashboard className="w-6 h-6 text-slate-500" />
        <p className="text-[13px] text-slate-300 font-medium">
          No past runs to show
        </p>
        <p className="text-[11.5px] text-slate-500 leading-relaxed max-w-[280px]">
          Every time this alert fires, we save a snapshot of the dashboard.
          The Logs view fills up from here.
        </p>
      </div>
    );
  }

  if (detailLoading) {
    return (
      <div className="flex items-center justify-center text-slate-400 text-[12px]">
        <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" />
        Loading snapshot…
      </div>
    );
  }

  if (detailError) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 text-center px-6 text-rose-300">
        <AlertTriangle className="w-5 h-5" />
        <p className="text-[12.5px]">{detailError}</p>
      </div>
    );
  }

  if (!snapshot) return null;

  // NEW: prefer the single PNG dashboard (GPT-5.4 Image 2) when the
  // snapshot carries it. The image is richer and far simpler to render
  // in a logs modal — no iframe / sandbox / postMessage dance, just an
  // <img> that scrolls natively with the pane. Fall back to the iframe'd
  // HTML when the image isn't present (legacy snapshots from before
  // image-gen, or runs where image generation failed / was disabled).
  if (snapshot.image_url) {
    return (
      <div className="relative bg-slate-950 overflow-auto">
        <div className="mx-auto max-w-[720px] p-4">
          <DashboardImage
            url={snapshot.image_url}
            generatedAt={snapshot.image_generated_at ?? null}
            model={snapshot.image_model ?? null}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-white overflow-hidden">
      <iframe
        // sandbox without allow-same-origin → scripts can run (Plotly,
        // Chart.js) but can't call out to xynth.com APIs with the
        // viewer's credentials. No allow-top-navigation → a runaway
        // dashboard can't redirect the user out of the modal.
        sandbox="allow-scripts"
        srcDoc={snapshot.html}
        className="w-full h-full border-0"
        title="Alert snapshot"
      />
    </div>
  );
}

function RunStatusDot({ status }: { status: string }) {
  if (status === "success") {
    return <CheckCircle2 className="w-3 h-3 text-emerald-400 shrink-0" strokeWidth={2.4} />;
  }
  if (status === "error") {
    return <XCircle className="w-3 h-3 text-rose-400 shrink-0" strokeWidth={2.4} />;
  }
  return <span className="w-3 h-3 rounded-full bg-slate-600 shrink-0" />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

function formatRunAt(iso: string): string {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diffSec = Math.max(1, Math.floor((Date.now() - t) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 86400 * 7) return `${Math.floor(diffSec / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatBytes(b: unknown): string {
  const n = typeof b === "number" ? b : Number(b);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}
